// Phase 10B — Calendar page restyled in the AutoMind 2050 design language.
// Logic, role gates, polling, and APIs are preserved verbatim — only JSX +
// classNames change. Empty/error → EmptyState. No mock fallbacks.

import React, { useState, useEffect } from 'react';
import { Calendar as CalendarIcon, Plus } from 'lucide-react';
import { PageHeader } from '../../components/ui/page-header';
import { TabBar } from '../../components/ui/tab-bar';
import { DataCard } from '../../components/ui/data-card';
import { EmptyState } from '../../components/ui/empty-state';
import { toast } from 'sonner';
import { appointmentsApi } from '../../api';
import { useAuth } from '../../auth/AuthContext';
import CreateAppointmentDialog from './CreateAppointmentDialog';
import CalendarGrid from './CalendarGrid';
import UpcomingAppointmentsList from './UpcomingAppointmentsList';
import AppointmentListCard from './AppointmentListCard';
import AppointmentFilters from './AppointmentFilters';
import { DEALER_TYPE_TERMINOLOGY } from './appointmentConstants';

const INITIAL_FILTERS = {
  status: '',
  appointment_type: '',
  needs_review: '',
  date_from: '',
  date_to: '',
};

const Calendar = () => {
  const { user } = useAuth();
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [sendingSMS, setSendingSMS] = useState({});
  const [filters, setFilters] = useState(INITIAL_FILTERS);
  const [runningReminders, setRunningReminders] = useState(false);
  const [view, setView] = useState('month');

  // Phase 6A — dealer-type terminology bias.
  const dealerType =
    user && user.role !== 'platform_admin' ? user.dealer_type : null;
  const terminology = dealerType ? DEALER_TYPE_TERMINOLOGY[dealerType] : null;
  const pageHeading = terminology
    ? dealerType === 'broker_remote'
      ? 'Callbacks'
      : 'Appointments'
    : 'Calendar';
  const canEditAll =
    user && ['owner', 'manager', 'admin', 'platform_admin'].includes(user.role);

  useEffect(() => {
    fetchAppointments();
    // eslint-disable-next-line
  }, [
    selectedDate,
    filters.status,
    filters.appointment_type,
    filters.needs_review,
    filters.date_from,
    filters.date_to,
  ]);

  // Auto-refresh appointments every 30 seconds for live updates
  useEffect(() => {
    const intervalId = setInterval(() => {
      fetchAppointments(false);
    }, 30000);
    return () => clearInterval(intervalId);
    // eslint-disable-next-line
  }, [selectedDate]);

  const fetchAppointments = async (showIndicator = true) => {
    try {
      if (showIndicator) {
        setLoading(true);
      } else {
        setIsRefreshing(true);
      }

      const startOfMonth = new Date(
        selectedDate.getFullYear(),
        selectedDate.getMonth(),
        1
      );
      const endOfMonth = new Date(
        selectedDate.getFullYear(),
        selectedDate.getMonth() + 1,
        0
      );

      const data = await appointmentsApi.list({
        startDate: startOfMonth.toISOString(),
        endDate: endOfMonth.toISOString(),
        status: filters.status || undefined,
        appointment_type: filters.appointment_type || undefined,
        needs_review: filters.needs_review || undefined,
        date_from: filters.date_from || undefined,
        date_to: filters.date_to || undefined,
      });
      setAppointments(Array.isArray(data) ? data : []);
      setLoadError(false);
    } catch (error) {
      console.error('Error fetching appointments:', error);
      setLoadError(true);
      if (showIndicator) {
        toast.error('Failed to fetch appointments');
      }
    } finally {
      setLoading(false);
      setIsRefreshing(false);
    }
  };

  const handleUpdateStatus = async (appointmentId, newStatus) => {
    try {
      await appointmentsApi.update(appointmentId, { status: newStatus });
      toast.success(`Appointment marked as ${newStatus}`);
      fetchAppointments();
    } catch (error) {
      console.error('Error updating appointment:', error);
      toast.error('Failed to update appointment');
    }
  };

  const handleDeleteAppointment = async (appointmentId) => {
    if (!window.confirm('Are you sure you want to delete this appointment?')) return;

    try {
      await appointmentsApi.remove(appointmentId);
      toast.success('Appointment has been deleted');
      fetchAppointments();
    } catch (error) {
      console.error('Error deleting appointment:', error);
      toast.error('Failed to delete appointment');
    }
  };

  const handleSendConfirmation = async (appointmentId) => {
    setSendingSMS((prev) => ({ ...prev, [appointmentId]: 'confirmation' }));
    try {
      const data = await appointmentsApi.sendConfirmationSms(appointmentId);
      if (data && data.success) {
        toast.success('Confirmation SMS sent');
      } else {
        toast.error((data && data.detail) || 'Failed to send SMS');
      }
    } catch (error) {
      console.error('Error sending SMS:', error);
      toast.error(error.message || 'Failed to send confirmation SMS');
    } finally {
      setSendingSMS((prev) => ({ ...prev, [appointmentId]: null }));
    }
  };

  const handleSendReminder = async (appointmentId) => {
    setSendingSMS((prev) => ({ ...prev, [appointmentId]: 'reminder' }));
    try {
      const data = await appointmentsApi.sendReminderSms(appointmentId);
      if (data && data.success) {
        toast.success('Reminder SMS sent');
        fetchAppointments();
      } else {
        toast.error((data && data.detail) || 'Failed to send SMS');
      }
    } catch (error) {
      console.error('Error sending SMS:', error);
      toast.error(error.message || 'Failed to send reminder SMS');
    } finally {
      setSendingSMS((prev) => ({ ...prev, [appointmentId]: null }));
    }
  };

  // Phase 6D — platform-admin trigger for the reminder scheduler.
  const handleRunDueReminders = async () => {
    setRunningReminders(true);
    try {
      const res = await appointmentsApi.runDueReminders();
      toast.success(
        `Reminders: ${res.sent} sent, ${res.skipped} skipped, ${res.failed} failed`
      );
      await fetchAppointments();
    } catch (err) {
      toast.error(err.message || 'Failed to run reminders');
    } finally {
      setRunningReminders(false);
    }
  };

  const changeMonth = (direction) => {
    const newDate = new Date(selectedDate);
    newDate.setMonth(selectedDate.getMonth() + direction);
    setSelectedDate(newDate);
  };

  return (
    <div className="p-6 max-w-[1400px] mx-auto">
      <PageHeader
        eyebrow="Schedule"
        title={pageHeading}
        description="Appointments across your dealership."
        actions={
          <div className="flex items-center gap-2">
            {isRefreshing && (
              <span className="text-[11px] uppercase tracking-[0.16em] text-ink-500 inline-flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-gold animate-pulse" />
                Syncing
              </span>
            )}
            {user?.role === 'platform_admin' && (
              <button
                type="button"
                className="btn-2050 btn-secondary"
                onClick={handleRunDueReminders}
                disabled={runningReminders}
              >
                {runningReminders ? 'Running…' : 'Run due reminders'}
              </button>
            )}
            <button
              type="button"
              className="btn-2050 btn-primary"
              onClick={() => setShowCreateDialog(true)}
            >
              <Plus className="w-4 h-4" />
              New appointment
            </button>
            <CreateAppointmentDialog
              open={showCreateDialog}
              onOpenChange={setShowCreateDialog}
              onCreated={fetchAppointments}
              hideTrigger
            />
          </div>
        }
      />

      <div className="mt-6">
        <TabBar
          value={view}
          onChange={setView}
          items={[
            { id: 'day', label: 'Day' },
            { id: 'week', label: 'Week' },
            { id: 'month', label: 'Month' },
          ]}
        />
      </div>

      <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <CalendarGrid
            selectedDate={selectedDate}
            appointments={appointments}
            onChangeMonth={changeMonth}
            onSetToday={() => setSelectedDate(new Date())}
            view={view}
          />
        </div>

        <UpcomingAppointmentsList
          appointments={appointments}
          sendingSMS={sendingSMS}
          onUpdateStatus={handleUpdateStatus}
          onDelete={handleDeleteAppointment}
          onSendConfirmation={handleSendConfirmation}
          onSendReminder={handleSendReminder}
        />
      </div>

      {/* All appointments */}
      <div className="mt-6">
        <DataCard
          title="All appointments"
          description={`${appointments.length} this month`}
          loading={loading && appointments.length === 0}
          empty={!loading && (loadError || appointments.length === 0)}
          emptyTitle={loadError ? 'No data yet' : 'No appointments scheduled'}
          emptyDescription={
            loadError
              ? 'Unable to load right now.'
              : 'Use the New appointment button above to add one.'
          }
          emptyIcon={CalendarIcon}
          bodyClassName="p-0"
        >
          <div className="px-6 pt-4">
            <AppointmentFilters filters={filters} onChange={setFilters} />
          </div>
          {appointments.length === 0 ? (
            <EmptyState
              title="No appointments"
              description="No appointments match the current filters."
            />
          ) : (
            <div className="p-6 pt-3 space-y-2">
              {appointments.map((apt) => (
                <AppointmentListCard
                  key={apt.id}
                  appointment={apt}
                  onUpdateStatus={handleUpdateStatus}
                  onDelete={handleDeleteAppointment}
                  canEdit={
                    canEditAll || (user && apt.assigned_user_id === user.id)
                  }
                  onChanged={fetchAppointments}
                />
              ))}
            </div>
          )}
        </DataCard>
      </div>
    </div>
  );
};

export default Calendar;
